import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Facebook, Instagram, ArrowRight, ChevronDown, Handshake } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const SOCIAL_LINKS = {
  facebook: 'https://www.facebook.com/middleman.secure',
  instagram: 'https://www.instagram.com/middleman.secure'
};

const Navbar = ({ user, onLogout, refreshUserData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t, language } = useLanguage();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const goToHome = () => {
    if (location.pathname !== '/') navigate('/');
    else window.scrollTo({ top: 0, behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const handleDashboardClick = async () => {
    const fresh = refreshUserData ? await refreshUserData() : user;
    const u = fresh || user;
    if (u.role === 'admin' || u.role === 'super_admin') navigate('/admin');
    else if (u.role === 'owner') navigate('/business');
    else navigate('/dashboard');
  };

  const mainMenuItems = [
    { label: t('nav.home'), action: goToHome },
    { label: language === 'fr' ? 'Fonctionnement' : 'How it works', action: () => scrollToSection('how-it-works') },
    { label: language === 'fr' ? 'Devenir Partenaire' : 'Become a Partner', action: () => navigate('/devenir-partenaire'), highlight: true },
  ];

  const dropdownMenuItems = [
    { label: t('nav.events'), action: () => navigate('/events') },
    { label: language === 'fr' ? 'Trouver des billets' : 'Find Tickets', action: () => navigate('/facebook-groups') },
    { label: language === 'fr' ? 'Guides des plateformes' : 'Platform Guides', action: () => navigate('/guides-plateformes') },
    { label: language === 'fr' ? 'Statistiques fraude' : 'Fraud Statistics', action: () => navigate('/statistiques-fraude') },
    { label: t('nav.faq'), action: () => navigate('/faq') },
    { label: t('nav.contact'), action: () => navigate('/contact') },
  ];

  const allMenuItems = [...mainMenuItems, ...dropdownMenuItems];

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-sm border-b border-gray-100' : 'bg-white/95 backdrop-blur-sm border-b border-gray-100'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-[68px]">
          {/* Logo */}
          <button onClick={goToHome} className="flex items-center flex-shrink-0" data-testid="navbar-logo">
            <img src="/logo-dark-cropped.png" alt="Middleman" className="h-4 sm:h-5 lg:h-6 w-auto" />
          </button>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-7">
            {mainMenuItems.map((item, i) => (
              <button
                key={i}
                onClick={item.action}
                className={`font-medium text-[15px] transition-colors whitespace-nowrap ${
                  item.highlight ? 'text-brand-700 hover:text-brand-800 flex items-center gap-1.5' : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                {item.highlight && <Handshake className="h-4 w-4" />}
                {item.label}
              </button>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-gray-700 hover:text-gray-900 font-medium transition-colors text-[15px]">
                {language === 'fr' ? 'Explorer' : 'Explore'}
                <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {dropdownMenuItems.map((item, i) => (
                  <DropdownMenuItem key={i} onClick={item.action} className="cursor-pointer">
                    {item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Desktop Auth */}
          <div className="hidden lg:flex items-center gap-3">
            <div className="flex items-center gap-1">
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" className="p-1.5 text-gray-400 hover:text-brand-700 transition-colors" aria-label="Facebook">
                <Facebook className="h-4 w-4" />
              </a>
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="p-1.5 text-gray-400 hover:text-brand-700 transition-colors" aria-label="Instagram">
                <Instagram className="h-4 w-4" />
              </a>
            </div>
            <div className="h-5 w-px bg-gray-200" />
            <LanguageSwitcher />
            {user ? (
              <>
                <button onClick={handleDashboardClick} className="text-gray-800 hover:text-gray-900 font-medium text-sm px-3 py-2 transition-colors" data-testid="navbar-dashboard-btn">
                  {t('nav.dashboard')}
                </button>
                <button onClick={onLogout} className="text-gray-600 hover:text-gray-900 font-medium text-sm px-3 py-2 transition-colors" data-testid="navbar-logout-btn">
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <>
                <button onClick={() => navigate('/auth')} className="text-gray-800 hover:text-gray-900 font-medium text-sm px-3 py-2 transition-colors" data-testid="navbar-login-btn">
                  {t('nav.login')}
                </button>
                <button onClick={() => navigate('/auth')} className="bg-gray-900 hover:bg-black text-white font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors" data-testid="navbar-create-account-btn">
                  {t('nav.createAccount')}
                </button>
              </>
            )}
          </div>

          {/* Mobile */}
          <div className="lg:hidden flex items-center gap-2">
            <button onClick={() => navigate('/auth')} className="flex items-center gap-1.5 px-3.5 py-2 bg-gray-900 hover:bg-black text-white text-xs font-semibold rounded-lg transition-all" data-testid="navbar-transfer-btn">
              <span>Transfert</span>
              <ArrowRight className="w-3 h-3" />
            </button>
            <LanguageSwitcher />
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 text-gray-700 hover:text-gray-900 rounded-md transition-colors" data-testid="navbar-mobile-toggle" aria-label="Menu">
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-100 py-3">
            <div className="flex flex-col gap-1">
              {allMenuItems.map((item, i) => (
                <button key={i} onClick={item.action} className={`text-left font-medium py-2.5 px-3 rounded-md text-sm transition-colors ${item.highlight ? 'text-brand-700 bg-brand-50 hover:bg-brand-100 flex items-center gap-2' : 'text-gray-700 hover:bg-gray-50'}`}>
                  {item.highlight && <Handshake className="h-4 w-4" />}
                  {item.label}
                </button>
              ))}
              <div className="border-t border-gray-100 pt-3 mt-2 px-3 space-y-2">
                {user ? (
                  <>
                    <button onClick={async () => { await handleDashboardClick(); setMobileMenuOpen(false); }} className="w-full text-center bg-gray-100 hover:bg-gray-200 text-gray-900 font-medium text-sm py-3 rounded-lg">
                      {t('nav.dashboard')}
                    </button>
                    <button onClick={() => { onLogout(); setMobileMenuOpen(false); }} className="w-full text-center text-gray-600 hover:text-gray-900 font-medium text-sm py-3">
                      {t('nav.logout')}
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => { navigate('/auth'); setMobileMenuOpen(false); }} className="w-full text-center bg-gray-100 hover:bg-gray-200 text-gray-900 font-medium text-sm py-3 rounded-lg">
                      {t('nav.login')}
                    </button>
                    <button onClick={() => { navigate('/auth'); setMobileMenuOpen(false); }} className="w-full text-center bg-gray-900 hover:bg-black text-white font-semibold text-sm py-3 rounded-lg">
                      {t('nav.createAccount')}
                    </button>
                  </>
                )}
                <div className="flex items-center justify-center gap-3 pt-4 mt-3 border-t border-gray-100">
                  <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" className="p-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors" aria-label="Facebook">
                    <Facebook className="h-5 w-5" />
                  </a>
                  <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="p-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors" aria-label="Instagram">
                    <Instagram className="h-5 w-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
